import { lazy } from 'react';
import { ACTIVITY_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { API_KEYS_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ASSETS_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { DOMAINS_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ORGANIZATION_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { ROLES_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { SECURITY_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { TEAM_FEATURE_GROUP } from '@socialgoodsoftware/frbac';
import { HomeSkeleton } from '../pages/home/HomeSkeleton';
import { AuthSkeleton } from '../pages/auth/AuthSkeleton';
import { DashboardSkeleton } from '../pages/dashboard/DashboardSkeleton';

import type { ComponentType } from 'react';
import type { ReactNode } from 'react';

type RouteArchetype = 'home' | 'auth' | 'dashboard';

export interface FeatureRoute {
  path: string;
  archetype: RouteArchetype;
  title: string;
  /** A page carries both; a redirect-only entry carries neither. */
  component?: ComponentType;
  skeleton?: ReactNode;
  redirectTo?: string;
  /** A leaf feature the member must hold to open the page directly. Any one of a list is enough. */
  featureGuid?: string | string[];
  /** The feature group that owns the page — the same one its navigation row is drawn from. */
  groupGuid?: string;
}

export const featureRoutes: FeatureRoute[] = [
  {
    path: '/',
    archetype: 'auth',
    title: 'Account',
    component: lazy(() => import('../pages/home/HomePage').then((module) => ({ default: module.HomePage }))),
    skeleton: <HomeSkeleton />,
  },
  {
    path: '/login',
    archetype: 'auth',
    title: 'Log in',
    component: lazy(() => import('../pages/auth/LoginPage').then((module) => ({ default: module.LoginPage }))),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/signup',
    archetype: 'auth',
    title: 'Create account',
    component: lazy(() => import('../pages/auth/SignupPage').then((module) => ({ default: module.SignupPage }))),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/forgot-password',
    archetype: 'auth',
    title: 'Reset password',
    redirectTo: '/reset-password',
  },
  {
    path: '/reset-password',
    archetype: 'auth',
    title: 'Reset password',
    component: lazy(() =>
      import('../pages/auth/ResetPasswordPage').then((module) => ({ default: module.ResetPasswordPage }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/login/mfa',
    archetype: 'auth',
    title: 'Verify MFA',
    component: lazy(() => import('../pages/auth/LoginMfaPage').then((module) => ({ default: module.LoginMfaPage }))),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/login/mfa/recovery',
    archetype: 'auth',
    title: 'Two-factor recovery',
    component: lazy(() =>
      import('../pages/auth/MfaRecoveryRequestedPage').then((module) => ({ default: module.MfaRecoveryRequestedPage }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/login/mfa/organization',
    archetype: 'auth',
    title: 'Choose an organization for two-factor recovery',
    component: lazy(() =>
      import('../pages/auth/MfaRecoveryOrganizationPage').then((module) => ({
        default: module.MfaRecoveryOrganizationPage,
      }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/login/organization',
    archetype: 'auth',
    title: 'Choose an organization',
    component: lazy(() =>
      import('../pages/auth/OrganizationSelectionPage').then((module) => ({
        default: module.OrganizationSelectionPage,
      }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/auth/no-organizations',
    archetype: 'auth',
    title: 'No organization',
    component: lazy(() =>
      import('../pages/auth/NoOrganizationsPage').then((module) => ({ default: module.NoOrganizationsPage }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/auth/enroll-mfa',
    archetype: 'auth',
    title: 'Set up two-factor authentication',
    component: lazy(() => import('../pages/auth/EnrollMfaPage').then((module) => ({ default: module.EnrollMfaPage }))),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/auth/organization',
    archetype: 'auth',
    title: 'Choose an organization',
    component: lazy(() =>
      import('../pages/auth/OrganizationSwitchPage').then((module) => ({ default: module.OrganizationSwitchPage }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/auth/mfa-reset',
    archetype: 'auth',
    title: 'Two-factor reset',
    component: lazy(() => import('../pages/auth/MfaResetPage').then((module) => ({ default: module.MfaResetPage }))),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/auth/confirm-email',
    archetype: 'auth',
    title: 'Confirm email change',
    component: lazy(() =>
      import('../pages/auth/ConfirmEmailPage').then((module) => ({ default: module.ConfirmEmailPage }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/register-phone-number',
    archetype: 'auth',
    title: 'Register phone number',
    component: lazy(() =>
      import('../pages/auth/RegisterPhonePage').then((module) => ({ default: module.RegisterPhonePage }))
    ),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/logout',
    archetype: 'auth',
    title: 'Signing out',
    component: lazy(() => import('../pages/auth/LogoutPage').then((module) => ({ default: module.LogoutPage }))),
    skeleton: <AuthSkeleton />,
  },
  {
    path: '/dashboard',
    archetype: 'dashboard',
    title: 'Dashboard',
    component: lazy(() =>
      import('../pages/dashboard/DashboardPage').then((module) => ({ default: module.DashboardPage }))
    ),
    skeleton: <DashboardSkeleton />,
  },
  {
    path: '/profile/:section?',
    archetype: 'dashboard',
    title: 'Profile',
    component: lazy(() => import('../pages/dashboard/AccountPage').then((module) => ({ default: module.AccountPage }))),
    skeleton: <DashboardSkeleton />,
  },
  {
    path: '/resources',
    archetype: 'dashboard',
    title: 'Resources',
    component: lazy(() =>
      import('../pages/dashboard/ResourcesPage').then((module) => ({ default: module.ResourcesPage }))
    ),
    skeleton: <DashboardSkeleton />,
  },
  {
    path: '/team/:section?',
    archetype: 'dashboard',
    title: 'Team',
    component: lazy(() => import('../pages/dashboard/TeamPage').then((module) => ({ default: module.TeamPage }))),
    skeleton: <DashboardSkeleton />,
    groupGuid: TEAM_FEATURE_GROUP.guid,
  },
  {
    path: '/roles',
    archetype: 'dashboard',
    title: 'Roles',
    redirectTo: '/roles/roles',
  },
  {
    path: '/roles/roles',
    archetype: 'dashboard',
    title: 'Roles',
    component: lazy(() => import('../pages/dashboard/RolesPage').then((module) => ({ default: module.RolesPage }))),
    skeleton: <DashboardSkeleton />,
    groupGuid: ROLES_FEATURE_GROUP.guid,
  },
  {
    path: '/roles/permissions',
    archetype: 'dashboard',
    title: 'Manage permissions',
    component: lazy(() =>
      import('../pages/dashboard/RolesPermissionsPage').then((module) => ({ default: module.RolesPermissionsPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: ROLES_FEATURE_GROUP.guid,
  },
  {
    path: '/roles/edit',
    archetype: 'dashboard',
    title: 'Edit role',
    component: lazy(() =>
      import('../pages/dashboard/RoleEditPage').then((module) => ({ default: module.RoleEditPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: ROLES_FEATURE_GROUP.guid,
  },
  {
    path: '/keys',
    archetype: 'dashboard',
    title: 'API keys',
    component: lazy(() => import('../pages/dashboard/ApiKeysPage').then((module) => ({ default: module.ApiKeysPage }))),
    skeleton: <DashboardSkeleton />,
    groupGuid: API_KEYS_FEATURE_GROUP.guid,
  },
  {
    path: '/domains/:section?',
    archetype: 'dashboard',
    title: 'Domains',
    component: lazy(() => import('../pages/dashboard/DomainsPage').then((module) => ({ default: module.DomainsPage }))),
    skeleton: <DashboardSkeleton />,
    groupGuid: DOMAINS_FEATURE_GROUP.guid,
  },
  {
    path: '/domains/view',
    archetype: 'dashboard',
    title: 'Domains',
    component: lazy(() =>
      import('../pages/dashboard/DomainDetailPage').then((module) => ({ default: module.DomainDetailPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: DOMAINS_FEATURE_GROUP.guid,
  },
  {
    path: '/keys/access',
    archetype: 'dashboard',
    title: 'API key access',
    component: lazy(() =>
      import('../pages/dashboard/ApiKeyAccessPage').then((module) => ({ default: module.ApiKeyAccessPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: API_KEYS_FEATURE_GROUP.guid,
  },
  {
    path: '/assets/:section?',
    archetype: 'dashboard',
    title: 'Assets',
    component: lazy(() =>
      import('../pages/dashboard/assets/AssetsPage').then((module) => ({ default: module.AssetsPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: ASSETS_FEATURE_GROUP.guid,
  },
  {
    path: '/activity',
    archetype: 'dashboard',
    title: 'Activity',
    component: lazy(() =>
      import('../pages/dashboard/ActivityPage').then((module) => ({ default: module.ActivityPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: ACTIVITY_FEATURE_GROUP.guid,
  },
  {
    path: '/organization',
    archetype: 'dashboard',
    title: 'Organization',
    component: lazy(() =>
      import('../pages/dashboard/OrganizationPage').then((module) => ({ default: module.OrganizationPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: ORGANIZATION_FEATURE_GROUP.guid,
  },
  {
    path: '/security',
    archetype: 'dashboard',
    title: 'Security',
    component: lazy(() =>
      import('../pages/dashboard/security/SecurityPage').then((module) => ({ default: module.SecurityPage }))
    ),
    skeleton: <DashboardSkeleton />,
    groupGuid: SECURITY_FEATURE_GROUP.guid,
  },
];
